import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import api from "../../lib/api";
import { toast } from "sonner";
import { Bell, X, CheckSquare, Clock, ArrowSquareOut, NotePencil } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import FollowupStatusSelect from "./FollowupStatusSelect";

const OUTCOMES = [
  ["call_connected", "Call Connected"],
  ["call_not_answered", "Call Not Answered"],
  ["customer_asked_to_call_later", "Asked to Call Later"],
  ["requirement_collected", "Requirement Collected"],
  ["quotation_requested", "Quotation Requested"],
  ["price_negotiation", "Price Negotiation"],
  ["site_visit_scheduled", "Site Visit Scheduled"],
  ["demo_scheduled", "Demo Scheduled"],
  ["customer_not_interested", "Not Interested"],
  ["wrong_number", "Wrong Number"],
  ["lead_converted", "Converted"],
  ["lead_lost", "Lost"],
];

function inr(n) {
  const v = Number(n) || 0;
  if (v === 0) return "Not added";
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN")}`;
}

function overdueLabel(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms <= 0) return "";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m overdue`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h overdue`;
  return `${Math.floor(hrs / 24)}d overdue`;
}

// Session-storage key for X-dismissed alerts. Each entry: {fid: timestampMs}.
// Dismissals expire after DISMISS_TTL_MS so a once-dismissed but still-overdue
// follow-up will re-appear after the TTL window (a reminder, not spam).
const DISMISS_STORAGE_KEY = "swiftech_followup_dismissed_v1";
const DISMISS_TTL_MS = 2 * 60 * 60 * 1000;  // 2 hours

function loadDismissedMap() {
  try {
    const raw = sessionStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    const now = Date.now();
    // Prune expired
    const fresh = {};
    for (const [fid, ts] of Object.entries(obj)) {
      if (typeof ts === "number" && (now - ts) < DISMISS_TTL_MS) fresh[fid] = ts;
    }
    return fresh;
  } catch { return {}; }
}

function persistDismissedMap(map) {
  try { sessionStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(map)); } catch { /* quota */ }
}

export default function FollowUpAlertPopup({ user }) {
  const isBDE = (user?.role || "").toLowerCase() === "bde";
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(() => loadDismissedMap());
  const [showDone, setShowDone] = useState(null);
  const [showSnooze, setShowSnooze] = useState(null);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user?.user_id || !isBDE) return;
    try {
      const r = await api.get(`${API}/leads/follow-ups/alerts`, {
        params: {
          requester_user_id: user.user_id,
          requester_name: user.name,
          requester_role: user.role,
        },
      });
      // Dedup defensively by follow_up_id — if the backend ever returns the same row twice
      // (lead split, snapshot race etc.) we collapse to a single card per id.
      const list = r.data || [];
      const seen = new Set();
      const deduped = [];
      for (const a of list) {
        const fid = a?.follow_up_id;
        if (!fid || seen.has(fid)) continue;
        seen.add(fid);
        deduped.push(a);
      }
      setAlerts(deduped);
    } catch (e) { /* silent */ }
  }, [user?.user_id, user?.name, user?.role, isBDE]);

  useEffect(() => {
    if (!isBDE) return undefined;
    refresh();
    timerRef.current = setInterval(refresh, 60000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [refresh, isBDE]);

  // Popup alerts are BDE-only. Other roles see follow-ups via Calendar / List / Drawer.
  if (!isBDE) return null;

  // Filter out X-dismissed (within TTL window) — even after backend re-sends the alert
  // on the next 60s poll, we don't show it again until the TTL elapses.
  const visible = alerts.filter((a) => !dismissed[a.follow_up_id]);
  if (!visible.length && !showDone && !showSnooze) return null;

  const dismiss = (fid) => {
    setDismissed((m) => {
      const next = { ...m, [fid]: Date.now() };
      persistDismissedMap(next);
      return next;
    });
  };

  return (
    <>
      {/* Bottom-right stack */}
      <div className="fixed bottom-4 right-4 z-[55] flex flex-col gap-2 max-w-sm" data-testid="followup-alert-stack">
        {visible.slice(0, 3).map((a) => (
          <AlertCard
            key={a.follow_up_id}
            a={a}
            onDone={() => setShowDone(a)}
            onSnooze={() => setShowSnooze(a)}
            onOpen={() => {
              // Navigate to /leads with query params Leads.js reads to auto-open the
              // followups drawer for this lead. SPA-friendly (react-router), not full reload.
              navigate(`/leads?openLead=${encodeURIComponent(a.lead_id)}&tab=followups`);
            }}
            onDismiss={() => dismiss(a.follow_up_id)}
          />
        ))}
        {visible.length > 3 && (
          <div className="bg-white border border-slate-300 px-3 py-1 text-[11px] text-slate-600 shadow-sm" data-testid="followup-alert-more">
            +{visible.length - 3} more
          </div>
        )}
      </div>
      {showDone && <DoneModal alert={showDone} user={user} onClose={() => setShowDone(null)} onDone={() => { setShowDone(null); refresh(); }} />}
      {showSnooze && <SnoozeModal alert={showSnooze} onClose={() => setShowSnooze(null)} onSnoozed={() => { setShowSnooze(null); refresh(); }} />}
    </>
  );
}

function AlertCard({ a, onDone, onSnooze, onOpen, onDismiss }) {
  const od = overdueLabel(a.next_follow_up_at);
  return (
    <div data-testid={`followup-alert-${a.follow_up_id}`} className={`bg-white border ${od ? "border-rose-300" : "border-blue-300"} shadow-lg`}>
      <div className={`px-3 py-2 flex items-center justify-between ${od ? "bg-rose-50" : "bg-blue-50"}`}>
        <div className="flex items-center gap-2">
          <Bell size={14} weight="fill" className={od ? "text-rose-600" : "text-blue-600"} />
          <span className={`text-[10px] uppercase tracking-wider font-bold ${od ? "text-rose-700" : "text-blue-700"}`}>
            {od || "Due Now"}
          </span>
        </div>
        <button onClick={onDismiss} className="p-0.5 text-slate-400 hover:text-slate-700" data-testid={`followup-alert-dismiss-${a.follow_up_id}`}><X size={12} /></button>
      </div>
      <div className="p-3 space-y-1">
        <div className="text-sm font-bold text-slate-800 truncate">{a.lead_name}</div>
        {a.company_name && <div className="text-xs text-slate-600 truncate">{a.company_name}</div>}
        <div className="text-[11px] text-slate-500 flex items-center gap-2">
          <span>{a.phone || "—"}</span>
          <span>·</span>
          <span>{a.assigned_to || "—"}</span>
        </div>
        <div className="text-[11px] text-slate-500 flex items-center gap-2">
          <span className="bg-slate-100 px-1.5 py-0.5">{a.status || a.outcome || "scheduled"}</span>
          <span className="font-bold text-emerald-700">{inr(a.estimated_amount)}</span>
        </div>
        {a.remarks && <div className="text-[11px] text-slate-600 italic truncate" title={a.remarks}>“{a.remarks}”</div>}
      </div>
      <div className="border-t border-slate-200 px-2 py-2 grid grid-cols-3 gap-1">
        <button data-testid={`followup-alert-done-${a.follow_up_id}`} onClick={onDone}
          className="text-[10px] uppercase tracking-wider font-bold bg-emerald-600 text-white px-2 py-1.5 hover:bg-emerald-700 inline-flex items-center justify-center gap-1">
          <CheckSquare size={11} /> Done
        </button>
        <button data-testid={`followup-alert-snooze-${a.follow_up_id}`} onClick={onSnooze}
          className="text-[10px] uppercase tracking-wider font-bold bg-amber-500 text-white px-2 py-1.5 hover:bg-amber-600 inline-flex items-center justify-center gap-1">
          <Clock size={11} /> Snooze
        </button>
        <button data-testid={`followup-alert-open-${a.follow_up_id}`} onClick={onOpen}
          className="text-[10px] uppercase tracking-wider font-bold bg-[#002FA7] text-white px-2 py-1.5 hover:bg-[#00227A] inline-flex items-center justify-center gap-1">
          <ArrowSquareOut size={11} /> Open
        </button>
      </div>
    </div>
  );
}

function DoneModal({ alert, user, onClose, onDone }) {
  const [outcome, setOutcome] = useState("call_connected");
  const [status, setStatus] = useState("");  // optional override; backend auto-maps from outcome if blank
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`${API}/follow-ups/${alert.follow_up_id}/done`, {
        outcome, note,
        ...(status ? { status } : {}),
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      toast.success("Follow-up marked done");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" data-testid="followup-done-modal">
      <div className="bg-white border border-slate-200 max-w-md w-full">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h3 className="text-lg font-bold inline-flex items-center gap-2"><CheckSquare size={18} className="text-emerald-600" />Mark Follow-up Done</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-500">{alert.lead_name} · {alert.company_name || "—"}</div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Outcome *</label>
            <select data-testid="followup-done-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)}
              className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1">
              {OUTCOMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Status (auto-mapped from outcome if blank)</label>
            <FollowupStatusSelect
              value={status}
              onChange={setStatus}
              user={user}
              testId="followup-done-status"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Completion Note</label>
            <textarea data-testid="followup-done-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              className="w-full rounded-none border border-slate-300 p-2 outline-none mt-1 text-sm" />
          </div>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button data-testid="followup-done-submit" onClick={submit} disabled={busy}
            className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-300">
            {busy ? "Saving…" : "Mark Done"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SnoozeModal({ alert, onClose, onSnoozed }) {
  const presets = [
    { key: "15min", label: "15 min", mins: 15 },
    { key: "30min", label: "30 min", mins: 30 },
    { key: "1h", label: "1 hour", mins: 60 },
    { key: "tomorrow", label: "Tomorrow 9 AM", custom: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d.toISOString(); } },
  ];
  const submit = async (iso) => {
    try {
      await api.post(`${API}/follow-ups/${alert.follow_up_id}/snooze`, { snoozed_until: iso });
      toast.success("Snoozed");
      onSnoozed();
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" data-testid="followup-snooze-modal">
      <div className="bg-white border border-slate-200 max-w-sm w-full" data-testid="followup-snooze-modal-content">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h3 className="text-lg font-bold inline-flex items-center gap-2"><Clock size={18} className="text-amber-600" />Snooze Follow-up</h3>
          <button onClick={onClose} data-testid="followup-snooze-modal-close" className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-2" data-testid="followup-snooze-presets">
          {presets.map((p) => (
            <button
              key={p.key}
              data-testid={`followup-snooze-preset-${p.key}`}
              onClick={() => submit(p.custom ? p.custom() : new Date(Date.now() + p.mins * 60000).toISOString())}
              className="px-3 py-3 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
